import { createApi } from '@reduxjs/toolkit/query/react';
import { RequestForQuotationComment } from '@state';
import { axiosBaseQuery } from '@client';
import { mainRestApi } from '../restApi/rest-api';

export type FetchCommentsQueryArg = {
  requestForQuotationId: string;
  expenseId: string;
  /** Client-only: after a successful GET, refetch request-case evaluation (e.g. mark thread read). */
  requestCaseId?: string;
  invalidateRequestCaseEvaluationAfterLoad?: boolean;
};

export const requestForQuotationApi = createApi({
  reducerPath: 'request-for-quotation-api',
  tagTypes: ['commentsApi-quotation'],
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    fetchComments: builder.query<
      RequestForQuotationComment[],
      FetchCommentsQueryArg
    >({
      providesTags: ['commentsApi-quotation'],
      query: ({ requestForQuotationId, expenseId }) => ({
        method: 'GET',
        url: `secured/request-for-quotation/${requestForQuotationId}/expense/${expenseId}/comments`
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        const { requestForQuotationId, expenseId } = queryArgs;
        return `${requestForQuotationId}_${expenseId}`;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (
            arg.invalidateRequestCaseEvaluationAfterLoad &&
            arg.requestCaseId
          ) {
            dispatch(
              mainRestApi.util.invalidateTags([
                {
                  type: 'REQUEST_CASE_EVALUATE',
                  id: arg.requestCaseId.toString()
                }
              ])
            );
          }
        } catch {
          // Comments request failed — do not touch evaluation cache
        }
      }
    }),
    postComment: builder.mutation<
      RequestForQuotationComment[],
      { requestForQuotationId: string; expenseId: string; commentText: string }
    >({
      invalidatesTags: ['commentsApi-quotation'],
      query: ({ requestForQuotationId, expenseId, commentText }) => ({
        method: 'POST',
        url: `secured/request-for-quotation/${requestForQuotationId}/expense/${expenseId}/comments`,
        data: { commentText }
      })
    })
  })
});

export type StateWithRequestForQuotationApi = {
  [requestForQuotationApi.reducerPath]: ReturnType<
    typeof requestForQuotationApi.reducer
  >;
};

// Export the hooks for the query endpoints
export const {
  useFetchCommentsQuery,
  usePostCommentMutation,
  useLazyFetchCommentsQuery
} = requestForQuotationApi;
